/**
 * Interface for storage backends. Strategies deal in raw strings only —
 * serialization (JSON, parseFloat, etc.) is handled by the hook/options layer.
 *
 * Implement this interface to add a new storage backend (e.g. IndexedDB).
 */
export interface StorageStrategy {
  /** Read a raw string value, or `undefined` if the key does not exist. */
  getItem(key: string): string | undefined
  /** Write a raw string value. */
  setItem(key: string, value: string): void
  /** Remove the key from storage. */
  deleteItem(key: string): void
}

/**
 * Converts typed values to/from the raw strings stored by a {@link StorageStrategy}.
 *
 * @example
 * ```ts
 * const numberSerializer: StorageSerializer<number> = {
 *   to: (value) => String(value),
 *   from: (value) => parseFloat(value),
 * }
 * ```
 */
export interface StorageSerializer<T> {
  to: (value: T) => string
  from: (value: string) => T
}

/**
 * Default JSON serializer using `JSON.stringify` / `JSON.parse`.
 * Used automatically by {@link useStorage} when no custom serializer is provided.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- JSON.parse returns any
export const jsonSerializer: StorageSerializer<any> = {
  to: (value) => JSON.stringify(value),
  from: (value) => JSON.parse(value),
}

/** Options for the {@link useStorage} hook. */
export interface UseStorageOptions<T> {
  /** Enable same-tab + cross-tab sync (only meaningful for localStorage). Default: false */
  sync?: boolean
  /** Custom serializer. Default: JSON */
  serializer?: StorageSerializer<T>
}
